import React from 'react';
import { ArrowRight } from 'lucide-react';

export const PageHero = ({ eyebrow, title, subtitle, primaryCta, secondaryCta }) => (
  <section className="relative overflow-hidden bg-gradient-to-br from-[#0D47A1] via-[#0b2f57] to-[#00897B] text-white">
    <div className="absolute inset-0 opacity-60 bg-[radial-gradient(circle_at_20%_20%,#1976D2_0,transparent_20%),radial-gradient(circle_at_80%_0,#29b6f6_0,transparent_18%),radial-gradient(circle_at_50%_60%,#00897B_0,transparent_22%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(255,255,255,0.05),transparent_30%)]" />
    <div className="container mx-auto px-8 lg:px-12 pt-28 lg:pt-36 pb-20 lg:pb-24 relative">
      <div className="max-w-3xl space-y-6">
        {eyebrow && (
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-black uppercase tracking-wide bg-white/10 border border-white/10">
            {eyebrow}
          </span>
        )}
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight text-transparent bg-clip-text bg-gradient-to-r from-white via-[#4FC3F7] to-white">
          {title}
        </h1>
        {subtitle && <p className="text-lg md:text-xl text-slate-200/80">{subtitle}</p>}
        {(primaryCta || secondaryCta) && (
          <div className="flex flex-col sm:flex-row gap-3">
            {primaryCta && (
              <a
                href={primaryCta.href}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-black text-lg bg-[#FF6B35] text-white hover:shadow-orange-500/40 transition-all"
              >
                {primaryCta.label}
                <ArrowRight className="w-5 h-5" />
              </a>
            )}
            {secondaryCta && (
              <a
                href={secondaryCta.href}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-black text-lg bg-[#1976D2] text-white hover:shadow-blue-500/30 transition-all"
              >
                {secondaryCta.label}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  </section>
);

export const SectionTitle = ({ eyebrow, title, description, mode = 'dark' }) => {
  const titleClass = mode === 'light' ? 'text-slate-900' : 'text-white';
  const descClass = mode === 'light' ? 'text-slate-600' : 'text-slate-200/80';
  const eyebrowClass = mode === 'light' ? 'text-[#0D47A1]' : 'text-white/70';

  return (
    <div className="flex flex-col gap-3 text-center mb-10">
      {eyebrow && <span className={`text-xs uppercase tracking-[0.3em] font-bold ${eyebrowClass}`}>{eyebrow}</span>}
      <h2 className={`text-3xl md:text-4xl font-black leading-tight ${titleClass}`}>{title}</h2>
      {description && <p className={`${descClass} max-w-3xl mx-auto text-lg`}>{description}</p>}
    </div>
  );
};

export const Card = ({ children, variant = 'dark' }) => (
  <div
    className={`rounded-2xl border p-6 ${
      variant === 'light'
        ? 'bg-white border-slate-200 text-slate-900 shadow-lg shadow-slate-200/60'
        : 'bg-white/5 border-white/10 text-white shadow-xl shadow-black/20'
    }`}
  >
    {children}
  </div>
);

export const Input = ({ label, ...props }) => (
  <label className="text-sm text-slate-200 space-y-1 block">
    {label}
    <input
      {...props}
      className="w-full mt-1 rounded-lg border border-white/10 bg-slate-950/40 px-3 py-2 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
    />
  </label>
);

export const Textarea = ({ label, ...props }) => (
  <label className="text-sm text-slate-200 space-y-1 block">
    {label}
    <textarea
      {...props}
      className="w-full rounded-lg border border-white/10 bg-slate-950/40 px-3 py-2 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
    />
  </label>
);
